import { Box, Divider, Flex, Grid, Spinner, Text } from '@chakra-ui/react';
import { ConcertCard } from 'components/ConcertCard/ConcertCard';
import { GameCard } from 'components/GameCard/GameCard';
import {
  ConcertResponse,
  InternationalEventResponse,
  MatchResponse,
  MatchState,
} from 'api/responseTypes';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { EventCategory } from '../hooks/useAllEvents';
import { useEventTracking } from 'hooks/useEventTracking';
import { internationalEventToConcertCard } from 'utils/internationalEvents';

interface EventListProps {
  concerts: ConcertResponse[];
  workshops: ConcertResponse[];
  matches: MatchResponse[];
  /** Rendered as concert-style cards alongside local events (non-compact mode). */
  international?: InternationalEventResponse[];
  selectedCategory: EventCategory;
  isLoading: boolean;
}

export const EventList = ({
  concerts,
  workshops,
  matches,
  international = [],
  selectedCategory,
  isLoading,
}: EventListProps) => {
  const navigate = useNavigate();
  const { eventTracking } = useEventTracking();

  const handleConcertClick = (concert: ConcertResponse) => {
    eventTracking.event.clicked({
      eventId: concert.eventId,
      eventName: concert.title,
      eventDate: concert.date ?? null,
      venue: concert.stadium ?? undefined,
      source: 'home_page',
    });
    navigate('/concert/' + concert.eventKey);
  };

  const handleInternationalClick = (event: InternationalEventResponse) => {
    eventTracking.internationalEvent.clicked({
      eventId: event.id,
      eventName: event.title,
      eventDate: event.date ?? null,
      venue: event.city ?? undefined,
      source: 'home_page',
    });
    navigate('/international-event/' + event.id);
  };

  const handleMatchClick = (match: MatchResponse) => {
    const eventName =
      match.teamA?.name && match.teamB?.name
        ? `${match.teamA.name} vs ${match.teamB.name}`
        : match.title || match.eventKey;

    eventTracking.event.clicked({
      eventId: match.eventId,
      eventName,
      eventDate: match.date ?? null,
      venue: match.stadium ?? undefined,
      source: 'home_page',
    });
    navigate('/event/' + match.eventKey);
  };

  if (isLoading) {
    return (
      <Flex align='center' justify='center' py={12}>
        <Spinner color='green.G50' size='xl' thickness='4px' />
      </Flex>
    );
  }

  const getEventsToShow = () => {
    switch (selectedCategory) {
      case 'concerts':
        return { concerts, workshops: [], matches: [] };
      case 'workshops':
        return { concerts: [], workshops, matches: [] };
      case 'matches':
        return { concerts: [], workshops: [], matches };
      case 'international':
        // Only international events (rendered separately below) belong here.
        return { concerts: [], workshops: [], matches: [] };
      case 'all':
      default:
        return { concerts, workshops, matches };
    }
  };

  const {
    concerts: concertsToShow,
    workshops: workshopsToShow,
    matches: matchesToShow,
  } = getEventsToShow();

  // International events surface in their own tab and, mixed in, under "all".
  // They only ever surface as ON_SALE, so there is no preview split.
  const internationalToShow =
    selectedCategory === 'all' || selectedCategory === 'international' ? international : [];

  const previewMatches = matchesToShow.filter((match) => match.state === MatchState.PREVIEW);
  const previewConcerts = concertsToShow.filter((concert) => concert.state === MatchState.PREVIEW);
  const previewWorkshops = workshopsToShow.filter(
    (workshop) => workshop.state === MatchState.PREVIEW,
  );

  const regularMatches = matchesToShow.filter((match) => match.state !== MatchState.PREVIEW);
  const regularConcerts = concertsToShow.filter((concert) => concert.state !== MatchState.PREVIEW);
  const regularWorkshops = workshopsToShow.filter(
    (workshop) => workshop.state !== MatchState.PREVIEW,
  );

  const previewItems = [
    ...previewMatches.map((match) => ({
      type: 'match' as const,
      id: `match-preview-${match.id}`,
      node: <GameCard {...match} onCardClick={() => handleMatchClick(match)} />,
    })),
    ...previewConcerts.map((concert) => ({
      type: 'concert' as const,
      id: `concert-preview-${concert.id}`,
      node: <ConcertCard {...concert} onCardClick={() => handleConcertClick(concert)} />,
    })),
    ...previewWorkshops.map((workshop) => ({
      type: 'workshop' as const,
      id: `workshop-preview-${workshop.id}`,
      node: (
        <ConcertCard
          {...workshop}
          typeOfEvent='workshop'
          onCardClick={() => handleConcertClick(workshop)}
        />
      ),
    })),
  ];

  const totalEventsCount =
    concertsToShow.length +
    workshopsToShow.length +
    matchesToShow.length +
    internationalToShow.length;
  const hasRegularEvents =
    regularMatches.length +
      regularConcerts.length +
      regularWorkshops.length +
      internationalToShow.length >
    0;

  const previewCardSize = {
    base: '320px',
    sm: '400px',
    md: '588px',
    lg: '384px',
  };

  const previewCardSizeWithPeek = {
    base: '270px',
    sm: '300px',
    md: '640px',
    lg: '420px',
  };

  const carouselCardSize = previewItems.length > 3 ? previewCardSizeWithPeek : previewCardSize;

  if (totalEventsCount === 0) {
    return (
      <Flex align='center' justify='center' direction='column' py={12} gap={4}>
        <Text textStyle='bodyLargeRegular' color='dark.D500' textAlign='center'>
          <FormattedMessage defaultMessage='لا توجد فعاليات متاحة' id='homepage.events.noEvents' />
        </Text>
      </Flex>
    );
  }

  return (
    <Box
      width='100%'
      display='flex'
      flexDirection='column'
      alignItems='center'
      gap={{ base: 6, md: 8 }}
    >
      {previewItems.length > 0 && (
        <Box
          width='100%'
          maxW='1200px'
          overflowX='auto'
          px={{ base: 1, md: 0 }}
          pb={3}
          dir='rtl'
          sx={{
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'dark.D200',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'dark.D300',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'dark.D200 transparent',
          }}
        >
          <Flex
            as='ul'
            listStyleType='none'
            gap={{ base: 4, md: 5 }}
            minW='fit-content'
            dir='rtl'
            alignItems='stretch'
          >
            {previewItems.map((item) => (
              <Box
                as='li'
                key={item.id}
                flex='0 0 auto'
                dir='ltr'
                w={carouselCardSize}
                minW={carouselCardSize}
                alignSelf={item.type === 'match' ? 'center' : undefined}
                display={item.type === 'match' ? 'block' : 'flex'}
              >
                {item.node}
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {hasRegularEvents && (
        <Grid
          templateColumns={{
            base: 'repeat(1, minmax(0, 1fr))',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          }}
          gap={{ base: 4, md: 6 }}
          justifyContent='center'
          as='ul'
          listStyleType='none'
          width='100%'
          maxW='1200px'
          alignItems='start'
          dir='rtl'
        >
          {regularMatches.map((match) => (
            <Box as='li' key={match.id} minW='0' width='100%' dir='ltr'>
              <GameCard {...match} onCardClick={() => handleMatchClick(match)} />
            </Box>
          ))}
          {regularMatches.length > 0 &&
            (regularConcerts.length > 0 || regularWorkshops.length > 0) && (
              <Box
                as='li'
                gridColumn={{ base: '1 / -1', md: '1 / -1', lg: '1 / -1' }}
                width='100%'
                py={{ base: 2, md: 3 }}
              >
                <Divider borderColor='dark.D200' />
              </Box>
            )}
          {regularConcerts.map((concert) => (
            <Box as='li' key={concert.id} minW='0' width='100%' dir='ltr'>
              <ConcertCard {...concert} onCardClick={() => handleConcertClick(concert)} />
            </Box>
          ))}
          {regularConcerts.length > 0 && regularWorkshops.length > 0 && (
            <Box
              as='li'
              gridColumn={{ base: '1 / -1', md: '1 / -1', lg: '1 / -1' }}
              width='100%'
              py={{ base: 2, md: 3 }}
            >
              <Divider borderColor='dark.D200' />
            </Box>
          )}
          {regularWorkshops.map((workshop) => (
            <Box as='li' key={workshop.id} minW='0' width='100%' dir='ltr'>
              <ConcertCard
                {...workshop}
                typeOfEvent='workshop'
                onCardClick={() => handleConcertClick(workshop)}
              />
            </Box>
          ))}
          {internationalToShow.length > 0 &&
            (regularMatches.length > 0 ||
              regularConcerts.length > 0 ||
              regularWorkshops.length > 0) && (
              <Box
                as='li'
                gridColumn={{ base: '1 / -1', md: '1 / -1', lg: '1 / -1' }}
                width='100%'
                py={{ base: 2, md: 3 }}
              >
                <Divider borderColor='dark.D200' />
              </Box>
            )}
          {internationalToShow.map((event) => (
            <Box as='li' key={`intl-${event.id}`} minW='0' width='100%' dir='ltr'>
              <ConcertCard
                {...internationalEventToConcertCard(event)}
                typeOfEvent='international'
                onCardClick={() => handleInternationalClick(event)}
              />
            </Box>
          ))}
        </Grid>
      )}
    </Box>
  );
};
